// lib/auth.ts
export interface AuthTokens {
    access_token: string;
    refresh_token: string;
    userType: 'platform' | 'app';
}

class AuthService {
    private static readonly ACCESS_TOKEN_KEY = 'token';
    private static readonly REFRESH_TOKEN_KEY = 'refresh_token';
    private static readonly TOKEN_EXPIRY_KEY = 'token_expiry';

    constructor() {
        // Initialize storage only on client side
        if (typeof window !== 'undefined') {
            this.setupTokenRefreshScheduler();
        }
    }

    private setupTokenRefreshScheduler() {
        // Check token expiry every minute
        setInterval(() => {
            if (this.shouldRefreshToken()) {
                this.refreshToken().catch(console.error);
            }
        }, 60000);
    }

    private shouldRefreshToken(): boolean {
        const expiry = localStorage.getItem(AuthService.TOKEN_EXPIRY_KEY);
        if (!expiry) return false;
        
        // Refresh if token will expire in the next 5 minutes
        const fiveMinutes = 5 * 60 * 1000;
        return Date.now() > (parseInt(expiry, 10) - fiveMinutes);
    }

    async login(username: string, password: string): Promise<void> {
        try {
            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, password, userType: 'platform' }),
            });

            if (!response.ok) {
                throw new Error('Login failed');
            }

            const data: AuthTokens = await response.json();
            this.storeTokens(data);
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    }

    storeTokens(tokens: AuthTokens): void {
        localStorage.setItem(AuthService.ACCESS_TOKEN_KEY, tokens.access_token);
        localStorage.setItem(AuthService.REFRESH_TOKEN_KEY, tokens.refresh_token);
        
        // Set expiry to 55 minutes from now (slightly less than the 1h expiry)
        const expiryTime = Date.now() + (55 * 60 * 1000);
        localStorage.setItem(AuthService.TOKEN_EXPIRY_KEY, expiryTime.toString());
    }

    async refreshToken(): Promise<void> {
        const refreshToken = localStorage.getItem(AuthService.REFRESH_TOKEN_KEY);
        
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        try {
            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/auth/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });

            if (!response.ok) {
                throw new Error('Token refresh failed');
            }

            const data: AuthTokens = await response.json();
            this.storeTokens(data);
        } catch (error) {
            console.error('Token refresh error:', error);
            this.logout();
            throw error;
        }
    }

    getAccessToken(): string | null {
        if (typeof window === 'undefined') return null;
        return localStorage.getItem(AuthService.ACCESS_TOKEN_KEY);
    }

    getRefreshToken(): string | null {
        if (typeof window === 'undefined') return null;
        return localStorage.getItem(AuthService.REFRESH_TOKEN_KEY);
    }

    isAuthenticated(): boolean {
        if (typeof window === 'undefined') return false;
        return !!this.getAccessToken();
    }

    isTokenExpired(): boolean {
        if (typeof window === 'undefined') return true;
        
        const expiry = localStorage.getItem(AuthService.TOKEN_EXPIRY_KEY);
        if (!expiry) return true;
        
        return Date.now() > parseInt(expiry, 10);
    }

    logout(): void {
        if (typeof window === 'undefined') return;
        
        localStorage.removeItem(AuthService.ACCESS_TOKEN_KEY);
        localStorage.removeItem(AuthService.REFRESH_TOKEN_KEY);
        localStorage.removeItem(AuthService.TOKEN_EXPIRY_KEY);
    }
}

// Export a singleton instance
export const authService = new AuthService();